import { useEffect, useState } from "react";
import { api, RAYG, RAYG_TEXT } from "../lib/api";
import { PageHeader } from "../components/Layout";

const META = {
  data: { overline: "Data Dimension", title: "Data Domains & Ownership", endpoint: "/data-domains",
    subtitle: "Data domains, owners, quality scores and known issues. Quality below 60 blocks reliable AI deployment." },
  technology: { overline: "Technology Dimension", title: "Application & Systems Rationalisation", endpoint: "/systems",
    subtitle: "Application inventory with retain / integrate / replace / retire decisions and risk levels." },
  governance: { overline: "Governance Dimension", title: "Risk & Control Map", endpoint: "/controls",
    subtitle: "Controls mapped to obligations and departments — effective, needing improvement, excessive or missing." },
};

const STATUS_COLORS = {
  retain: "#22C55E", integrate: "#3B82F6", replace: "#F97316", retire: "#EF4444",
  effective: "#22C55E", needs_improvement: "#F59E0B", excessive: "#F97316", missing: "#EF4444",
  low: "#22C55E", medium: "#F59E0B", high: "#EF4444",
};

export default function Registry({ kind }) {
  const meta = META[kind];
  const [items, setItems] = useState([]);
  const [depts, setDepts] = useState([]);

  useEffect(() => {
    setItems([]);
    Promise.all([api.get(meta.endpoint), api.get("/departments")]).then(([i, d]) => { setItems(i.data); setDepts(d.data); });
  }, [kind]);

  const deptByKey = Object.fromEntries(depts.map((d) => [d.key, d]));
  const pill = (v) => (
    <span className="text-[11px] px-2 py-0.5 rounded-full text-white capitalize" style={{ background: STATUS_COLORS[v] || "#999" }}>{String(v).replace("_", " ")}</span>
  );

  return (
    <div className="canvas-grain min-h-screen pb-16" data-testid={`registry-page-${kind}`}>
      <PageHeader overline={meta.overline} title={meta.title} subtitle={meta.subtitle} />
      <div className="px-8">
        <div className="bg-white border border-black/10 rounded-sm overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-[#0A0A0A] text-[#D4AF37] text-left text-[11px] uppercase tracking-[0.12em]">
                {kind === "data" && <><th className="px-4 py-3">Domain</th><th className="px-4 py-3">Owner</th><th className="px-4 py-3">Department</th><th className="px-4 py-3">Quality</th><th className="px-4 py-3">Issues</th></>}
                {kind === "technology" && <><th className="px-4 py-3">System</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Departments</th><th className="px-4 py-3">Decision</th><th className="px-4 py-3">Risk</th><th className="px-4 py-3">Notes</th></>}
                {kind === "governance" && <><th className="px-4 py-3">Control</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Department</th><th className="px-4 py-3">Obligation</th><th className="px-4 py-3">Status</th></>}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-black/5 last:border-0 hover:bg-[#FDFBF7] transition-colors" data-testid="registry-row">
                  {kind === "data" && <>
                    <td className="px-4 py-3 font-medium">{it.name}</td>
                    <td className="px-4 py-3">{it.owner}</td>
                    <td className="px-4 py-3"><span style={{ color: deptByKey[it.department_key]?.color }}>● </span>{deptByKey[it.department_key]?.name}</td>
                    <td className="px-4 py-3"><span className="font-mono text-[12px] px-2 py-0.5 rounded-sm" style={{ background: RAYG(it.quality_score), color: RAYG_TEXT(it.quality_score) }}>{it.quality_score}</span></td>
                    <td className="px-4 py-3 text-black/60">{(it.issues || []).join("; ")}</td>
                  </>}
                  {kind === "technology" && <>
                    <td className="px-4 py-3 font-medium">{it.name}</td>
                    <td className="px-4 py-3">{it.category}</td>
                    <td className="px-4 py-3">{(it.department_keys || []).map((dk) => <span key={dk} className="inline-block w-3 h-3 rounded-sm mr-1" title={deptByKey[dk]?.name} style={{ background: deptByKey[dk]?.color }} />)}</td>
                    <td className="px-4 py-3">{pill(it.status)}</td>
                    <td className="px-4 py-3">{pill(it.risk)}</td>
                    <td className="px-4 py-3 text-black/60">{it.notes}</td>
                  </>}
                  {kind === "governance" && <>
                    <td className="px-4 py-3 font-medium">{it.name}</td>
                    <td className="px-4 py-3">{it.type}</td>
                    <td className="px-4 py-3"><span style={{ color: deptByKey[it.department_key]?.color }}>● </span>{deptByKey[it.department_key]?.name}</td>
                    <td className="px-4 py-3 text-black/60">{it.obligation}</td>
                    <td className="px-4 py-3">{pill(it.status)}</td>
                  </>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
